package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import org.example.project.settings.*
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private fun nextDefinitionId(items: List<All>): String =
    ((items.maxOfOrNull { it.akquiseDefinitionId.toIntOrNull() ?: 0 } ?: 0) + 1).toString()

internal fun addFlowSection(items: List<All>): List<All> {
    val next = items.maxOfOrNull { it.akquiseDefinitionTypPrio }?.plus(10) ?: 10
    val name = generateSequence(1) { it + 1 }.map { "Neuer Abschnitt $it" }
        .first { candidate -> items.none { it.akquiseDefinitionTyp == candidate } }
    val id = nextDefinitionId(items)
    return items + All(id = id, akquiseDefinitionId = id, akquiseTyp = "Call", akquiseDefinitionTyp = name,
        akquiseDefinitionTypPrio = next, akquiseDefinitionPrio = 10, akquiseDefinitionText = "Neue Frage", akquiseDefinitionAktiv = true)
}

private fun List<All>.replaceDefinition(id: String, change: (All) -> All): List<All> =
    map { if (it.akquiseDefinitionId == id) change(it) else it }

internal fun All.flowAntworten(): List<String> = akquiseDefinitionAntworten.ifEmpty {
    listOf(akquiseDefinitionAntwortLabel1, akquiseDefinitionAntwortLabel2, akquiseDefinitionAntwortLabel3).filter { it.isNotBlank() }
}

internal fun All.flowReaktionen(): List<String> = akquiseDefinitionReaktionen.ifEmpty {
    listOf(akquiseDefinitionReaktion1, akquiseDefinitionReaktion2, akquiseDefinitionReaktion3).filter { it.isNotBlank() }
}

@Composable
internal fun AkquiseFlowEditor(
    links: List<All>, rechts: List<All>,
    scripts: List<All>,
    onLinksChange: (List<All>) -> Unit, onRechtsChange: (List<All>) -> Unit,
    showCheckboxes: Boolean = true,
    minimal: Boolean = false
) {
    var previewChecks by remember { mutableStateOf<Set<String>>(emptySet()) }
    var picker by remember { mutableStateOf<ScriptPickerTarget?>(null) }
    val resolved = resolveScriptDefinitions(links, scripts)
    Div({ style { width(100.percent); height(100.percent); minHeight(0.px); display(DisplayStyle.Grid); gridTemplateColumns(if (showCheckboxes) "minmax(0, 7fr) minmax(300px, 3fr)" else "minmax(0, 1fr)") } }) {
        Div({ style { overflowY("auto"); padding(18.px); boxSizing("border-box"); property("border-right", if (showCheckboxes) "1px solid rgba(255,255,255,.16)" else "none") } }) {
            if (!showCheckboxes && !minimal) Div({ style { textAlign("center"); fontWeight("700"); marginBottom(18.px) } }) { Text("Gesprächsflow") }
            val sections = resolved.groupBy { it.akquiseDefinitionTyp }.entries.sortedBy { it.value.minOf { row -> row.akquiseDefinitionTypPrio } }
            sections.forEach { (name, entries) ->
                val ordered = entries.sortedBy { it.akquiseDefinitionPrio }
                var sectionDeleteHovered by remember { mutableStateOf(false) }
                Div({ style { padding(16.px); marginBottom(18.px); borderRadius(12.px); property("border", if (sectionDeleteHovered) "1px solid #ff3b3b" else "1px solid rgba(255,255,255,.10)") } }) {
                    Div({ style { display(DisplayStyle.Flex); gap(10.px); alignItems(AlignItems.Center); marginBottom(14.px) } }) {
                        EditorInput(name, "Abschnittsname", { value ->
                            onLinksChange(links.map { if (it.akquiseDefinitionTyp == name) it.copy(akquiseDefinitionTyp = value) else it })
                        }, grow = true, centered = true)
                        EditorClose("Abschnitt entfernen", { onLinksChange(links.filterNot { it.akquiseDefinitionTyp == name }) }, { sectionDeleteHovered = it })
                    }
                    ordered.forEachIndexed { index, row ->
                        val id = row.akquiseDefinitionId
                        var textDeleteHovered by remember { mutableStateOf(false) }
                        Div({ style {
                            padding(14.px); marginBottom(12.px); borderRadius(10.px)
                            backgroundColor(if (index % 2 == 0) Color("rgba(255,255,255,.055)") else Color("rgba(255,255,255,.025)"))
                            property("border", if (textDeleteHovered) "1px solid #ff3b3b" else "1px solid rgba(255,255,255,.08)")
                        } }) {
                            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(10.px) } }) {
                                Span { Text("Text / Frage") }
                                EditorClose("Text entfernen", { onLinksChange(links.filterNot { it.akquiseDefinitionId == id }) }, { textDeleteHovered = it })
                            }
                            Div({ style { minHeight(50.px); padding(10.px); property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere"); property("border", "1px solid rgba(255,255,255,.18)") } }) {
                                Text(row.akquiseDefinitionText)
                            }
                            Div({ style { display(DisplayStyle.Grid); gridTemplateColumns("repeat(2, minmax(0, 1fr))"); gap(16.px); marginTop(14.px) } }) {
                                EditorScriptChoices("Antworten", row.flowAntworten(), row.akquiseDefinitionAntwortTypen, { index ->
                                    onLinksChange(links.replaceDefinition(id) { item -> item.copy(
                                        akquiseDefinitionAntworten = item.flowAntworten().filterIndexed { i, _ -> i != index },
                                        akquiseDefinitionAntwortIds = item.akquiseDefinitionAntwortIds.filterIndexed { i, _ -> i != index },
                                        akquiseDefinitionAntwortTypen = item.akquiseDefinitionAntwortTypen.filterIndexed { i, _ -> i != index },
                                        akquiseDefinitionAntwortLabel1 = "", akquiseDefinitionAntwortLabel2 = "", akquiseDefinitionAntwortLabel3 = "") })
                                }, { picker = ScriptPickerTarget("Antwort", id, name) })
                                EditorScriptChoices("Reaktionen", row.flowReaktionen(), emptyList(), { index ->
                                    onLinksChange(links.replaceDefinition(id) { item -> item.copy(
                                        akquiseDefinitionReaktionen = item.flowReaktionen().filterIndexed { i, _ -> i != index },
                                        akquiseDefinitionReaktionIds = item.akquiseDefinitionReaktionIds.filterIndexed { i, _ -> i != index },
                                        akquiseDefinitionReaktion1 = "", akquiseDefinitionReaktion2 = "", akquiseDefinitionReaktion3 = "") })
                                }, { picker = ScriptPickerTarget("Reaktion", id, name) })
                            }
                        }
                    }
                    EditorAdd("Text hinzufügen", {
                        picker = ScriptPickerTarget("Text", "", name)
                    }, showLabel = true)
                }
            }
            EditorAdd("Abschnitt hinzufügen", { picker = ScriptPickerTarget("Abschnitt", "", "") }, showLabel = !minimal)
        }
        if (showCheckboxes) Div({ style { overflowY("auto"); padding(18.px); boxSizing("border-box") } }) {
            Div({ style { padding(16.px); borderRadius(12.px); property("border", "1px solid rgba(255,255,255,.10)") } }) {
                    Div({ style { textAlign("center"); fontWeight("700"); marginBottom(14.px) } }) { Text("Checkboxen") }
                    rechts.sortedBy { it.akquiseDefinitionPrio }.forEach { row ->
                        var checkboxDeleteHovered by remember { mutableStateOf(false) }
                        Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); marginBottom(10.px); padding(7.px); borderRadius(8.px); property("border", if (checkboxDeleteHovered) "1px solid #ff3b3b" else "1px solid transparent") } }) {
                            LumaticaCheckbox(checked = row.akquiseDefinitionId in previewChecks, onChange = { checked ->
                                previewChecks = if (checked) previewChecks + row.akquiseDefinitionId else previewChecks - row.akquiseDefinitionId
                            })
                            EditorInput(row.akquiseDefinitionBezeichnung, "Checkbox-Name", { value ->
                                onRechtsChange(rechts.replaceDefinition(row.akquiseDefinitionId) { it.copy(akquiseDefinitionBezeichnung = value) })
                            }, grow = true)
                            EditorClose("Checkbox entfernen", { onRechtsChange(rechts.filterNot { it.akquiseDefinitionId == row.akquiseDefinitionId }) }, { checkboxDeleteHovered = it })
                        }
                    }
                    EditorAdd("Checkbox hinzufügen", {
                        val id = nextDefinitionId(rechts)
                        onRechtsChange(rechts + All(id = id, akquiseDefinitionId = id, akquiseDefinitionBereich = "Checkboxen",
                            akquiseDefinitionBezeichnung = "Neue Checkbox", akquiseDefinitionPrio = (rechts.maxOfOrNull { it.akquiseDefinitionPrio } ?: 0) + 10,
                            akquiseDefinitionAktiv = true))
                    })
            }
        }
    }
    picker?.let { target ->
        val candidates = scripts.filter { when (target.kind) {
            "Text", "Abschnitt" -> it.scriptTyp == "Text" || it.scriptTyp == "Frage"
            "Antwort" -> it.scriptTyp == "Antwort" || it.scriptTyp == "Einwand"
            else -> it.scriptTyp == target.kind
        } }
        ScriptPickerDialog(candidates, onClose = { picker = null }) { selected ->
            when (target.kind) {
                "Abschnitt" -> {
                    val added = addFlowSection(links)
                    onLinksChange(added.dropLast(1) + added.last().copy(akquiseDefinitionScriptId = selected.id, akquiseDefinitionText = selected.scriptInhalt))
                }
                "Text" -> {
                    val entries = links.filter { it.akquiseDefinitionTyp == target.section }
                    val id = nextDefinitionId(links)
                    onLinksChange(links + All(id = id, akquiseDefinitionId = id, akquiseTyp = "Call", akquiseDefinitionTyp = target.section,
                        akquiseDefinitionTypPrio = entries.first().akquiseDefinitionTypPrio,
                        akquiseDefinitionPrio = (entries.maxOfOrNull { it.akquiseDefinitionPrio } ?: 0) + 10,
                        akquiseDefinitionScriptId = selected.id, akquiseDefinitionText = selected.scriptInhalt, akquiseDefinitionAktiv = true))
                }
                "Antwort" -> onLinksChange(links.replaceDefinition(target.rowId) { item ->
                    val values = item.flowAntworten()
                    item.copy(akquiseDefinitionAntworten = values + selected.scriptInhalt,
                        akquiseDefinitionAntwortIds = item.akquiseDefinitionAntwortIds.ifEmpty { List(values.size) { "" } } + selected.id,
                        akquiseDefinitionAntwortTypen = List(values.size) { item.akquiseDefinitionAntwortTypen.getOrNull(it) ?: "Antwort" } + selected.scriptTyp,
                        akquiseDefinitionAntwortLabel1 = "", akquiseDefinitionAntwortLabel2 = "", akquiseDefinitionAntwortLabel3 = "")
                })
                "Reaktion" -> onLinksChange(links.replaceDefinition(target.rowId) { item ->
                    val values = item.flowReaktionen()
                    item.copy(akquiseDefinitionReaktionen = values + selected.scriptInhalt,
                        akquiseDefinitionReaktionIds = item.akquiseDefinitionReaktionIds.ifEmpty { List(values.size) { "" } } + selected.id,
                        akquiseDefinitionReaktion1 = "", akquiseDefinitionReaktion2 = "", akquiseDefinitionReaktion3 = "")
                })
            }
            picker = null
        }
    }
}

private data class ScriptPickerTarget(val kind: String, val rowId: String, val section: String)

@Composable
internal fun EditorInput(value: String, hint: String, onChange: (String) -> Unit, grow: Boolean = false, centered: Boolean = false) {
    Input(InputType.Text, attrs = {
        value(value); attr("placeholder", hint); attr("aria-label", hint); onInput { onChange(it.value) }
        style { if (grow) flex("1"); minWidth(0.px); height(38.px); padding(0.px, 10.px); boxSizing("border-box"); backgroundColor(Color.transparent); property("border", "1px solid rgba(255,255,255,.18)"); color(Color.white); if (centered) textAlign("center") }
    })
}

@Composable
internal fun EditorClose(label: String, onClick: () -> Unit, onHover: (Boolean) -> Unit = {}) {
    var hovered by remember { mutableStateOf(false) }
    Button(attrs = {
        attr("aria-label", label); attr("title", label)
        onMouseEnter { hovered = true; onHover(true) }
        onMouseLeave { hovered = false; onHover(false) }
        onClick { hovered = false; onHover(false); onClick() }
        style { width(30.px); height(30.px); flexShrink(0); padding(5.px); backgroundColor(Color.transparent); property("border", "none"); cursor("pointer") }
    }) {
        Img(src = "images/miniclose.svg", attrs = {
            style { width(18.px); height(18.px); property("filter", if (hovered) UISettings.ICON_FILTER_ACCENT else "brightness(0) invert(1)") }
        })
    }
}

@Composable
internal fun EditorAdd(label: String, onClick: () -> Unit, showLabel: Boolean = false) {
    Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center) } }) {
        Button(attrs = {
            attr("aria-label", label); attr("title", label)
            onClick { onClick() }
            style {
                if (!showLabel) width(34.px)
                height(34.px); padding(6.px)
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); gap(7.px)
                backgroundColor(Color.transparent); property("border", "none"); color(Color.white); cursor("pointer")
            }
        }) {
            Img(src = "images/add.svg", attrs = {
                style { width(22.px); height(22.px); property("filter", "brightness(0) invert(1)") }
            })
            if (showLabel) Text(label)
        }
    }
}

@Composable
private fun EditorScriptChoices(title: String, values: List<String>, types: List<String>, onRemove: (Int) -> Unit, onAdd: () -> Unit) {
    Div({ style { minWidth(0.px) } }) {
        Div({ style { marginBottom(8.px); fontWeight("600"); textAlign("center") } }) { Text(title) }
        values.forEachIndexed { index, value ->
            Div({ style { display(DisplayStyle.Flex); gap(6.px); marginBottom(8.px) } }) {
                Div({ style { flex("1"); minWidth(0.px); minHeight(54.px); padding(8.px, 10.px); boxSizing("border-box"); property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere"); property("border", "1px solid rgba(255,255,255,.18)") } }) {
                    if (types.getOrNull(index) == "Einwand") Span({ style { fontWeight("700") } }) { Text("Einwand: ") }
                    Text(value)
                }
                EditorClose("${if (title == "Antworten") "Antwort" else "Reaktion"} entfernen", {
                    onRemove(index)
                })
            }
        }
        EditorAdd("${if (title == "Antworten") "Antwort" else "Reaktion"} hinzufügen", onAdd)
    }
}

@Composable
private fun ScriptPickerDialog(candidates: List<All>, onClose: () -> Unit, onSelect: (All) -> Unit) {
    var query by remember { mutableStateOf("") }
    var selectedType by remember { mutableStateOf<String?>(null) }
    val types = candidates.map { it.scriptTyp }.distinct().sorted()
    val visible = candidates.filter { entry ->
        (selectedType == null || entry.scriptTyp == selectedType) &&
            (query.isBlank() || entry.scriptInhalt.contains(query.trim(), ignoreCase = true))
    }
    PopupEngineCard(windowKey = "vertrieb-script-picker", zIndex = POPUP_ENGINE_MODAL_Z_INDEX + 50,
        widthPx = 720, heightPx = 600, minWidthPx = 450, minHeightPx = 320,
        centered = true, viewportAnchored = true, resizable = true, allowContentOverflow = true, onClose = onClose) {
        Div({ style { height(100.percent); padding(20.px); boxSizing("border-box"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px) } }) {
            H3({ style { margin(0.px); textAlign("center") } }) { Text("Text auswählen") }
            Div({ style { display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.Center) } }) {
                Div({ style { width(170.px); flexShrink(0) } }) {
                    AppDropdown(
                        options = listOf(All(key = "__alle__", label = "Alle Typen")) + types.map { All(key = it, label = it) },
                        selectedValue = selectedType ?: "__alle__",
                        onChange = { selectedType = it.takeUnless { value -> value == "__alle__" } },
                        placeholder = "Alle Typen",
                        appearance = AppDropdownAppearance.Field,
                        menuMode = AppDropdownMenuMode.Attached,
                        attrs = { attr("aria-label", "Typ filtern") }
                    )
                }
                EditorInput(query, "Text suchen", { query = it }, grow = true)
            }
            Div({ style { flex("1"); minHeight(0.px); overflowY("auto") } }) {
                visible.forEachIndexed { index, entry ->
                        Button(attrs = {
                            attr("aria-label", "${entry.scriptTyp}: ${entry.scriptInhalt}")
                            onClick { onSelect(entry) }
                            style {
                                width(100.percent); display(DisplayStyle.Grid); gridTemplateColumns("110px minmax(0, 1fr)")
                                gap(16.px); alignItems(AlignItems.Center); padding(14.px); marginBottom(8.px)
                                boxSizing("border-box"); borderRadius(10.px)
                                backgroundColor(if (index % 2 == 0) Color("rgba(255,255,255,.055)") else Color("rgba(255,255,255,.025)"))
                                property("border", "none"); property("border-bottom", "1px solid rgba(255,255,255,.10)")
                                color(Color.white); cursor("pointer")
                            }
                        }) {
                            Span({ style { textAlign("left"); fontSize(14.px); fontWeight("600") } }) { Text(entry.scriptTyp) }
                            Span({ style {
                                minWidth(0.px); padding(10.px, 12.px); textAlign("center"); fontSize(18.px)
                                lineHeight("1.45"); fontWeight("650"); property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere")
                            } }) { Text(entry.scriptInhalt) }
                        }
                }
                if (visible.isEmpty()) P { Text(if (candidates.isEmpty()) "In der Script-Tabelle sind noch keine passenden Einträge vorhanden." else "Keine passenden Texte gefunden.") }
            }
        }
    }
}
